import { useEffect, useState } from 'react'

import './_accountSettings.scss'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'

import InputCard from 'components/Form/InputCard/InputCard'
import { addToast } from 'components/Toast/Toast'
import FixedBottomButton from 'components/UI/FixedBottomButton/FixedBottomButton'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { Icons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { SuccessfulChange } from 'pages/settings/features/SuccessfulChange'
import {
    changeUsername,
    checkUsername,
    toggleUserNameForwarding
} from 'services/endpoints/settings/account/profileSettings'
import { useSetUserProfile, useUserProfile } from 'store'

const USERNAME_MIN_LENGTH = 4
const USERNAME_MAX_LENGTH = 20
const USERNAME_REGEX = /^[a-zA-Z0-9]+$/

export const ChangeUsername = () => {
    const [newUsername, setNewUsername] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [usernameTaken, setUsernameTaken] = useState(false)
    const [usernameLengthError, setUsernameLengthError] = useState(false)
    const [usernameForwarding, setUsernameForwarding] = useState(false)
    const [changeType, setChangeType] = useState<
        'username' | 'forwarding' | 'both'
    >('username')

    const userData = useUserProfile()!
    const navigate = useNavigate()
    const { t } = useTranslation()

    useEffect(() => {
        if (userData) {
            setNewUsername(userData.username)
            setUsernameForwarding(userData.forwarding_username)
        }
    }, [userData])


    // USERNAME CHECK
useEffect(() => {
    const usernameChanged = newUsername !== userData.username
    const usernameValid = /^[a-zA-Z0-9]{4,20}$/.test(newUsername)

    setUsernameTaken(false)

    if (!usernameChanged || !usernameValid) {
        return
    }

    const timeout = setTimeout(async () => {
        try {
            await checkUsername(newUsername)

            setUsernameTaken(false)
        } catch (err: any) {
            const usernameErrors =
                err?.response?.data?.error?.errors?.username

            if (Array.isArray(usernameErrors) && usernameErrors.length > 0) {
                setUsernameTaken(true)
                return
            }

            console.error('Username availability check failed', err)
        }
    }, 500)

    return () => clearTimeout(timeout)
}, [newUsername, userData.username])


    useEffect(() => {
        const timeout = setTimeout(() => {
            const usernameChanged = newUsername !== userData.username

            setUsernameLengthError(
                usernameChanged &&
                    newUsername.length > 0 &&
                    newUsername.length < USERNAME_MIN_LENGTH
            )
        }, 500)

        return () => clearTimeout(timeout)
    }, [newUsername, userData.username])

    const usernameChanged = newUsername !== userData.username

    const usernameValid =
        newUsername.length >= USERNAME_MIN_LENGTH &&
        newUsername.length <= USERNAME_MAX_LENGTH &&
        USERNAME_REGEX.test(newUsername)

    const updateUsername = useMutation(
        async (data: { username: string }) =>
            await changeUsername({
                username: data.username
            }),
        {
            onError: err => {
                console.error(err)
                setUsernameTaken(true)
            },
            onSuccess: () => {
                useSetUserProfile({
                    username: newUsername,
                    forwarding_username: usernameForwarding
                })

                if (
                    usernameForwarding !==
                    userData.forwarding_username
                ) {
                    setChangeType('both')
                }

                setCurrentPage(2)
            }
        }
    )

    const toggleForwardingMutation = useMutation(
        toggleUserNameForwarding,
        {
            onSuccess: (_response, newValue) => {
                useSetUserProfile({
                    forwarding_username: newValue
                })
            },
            onError: () => {
                setUsernameForwarding(
                    userData.forwarding_username
                )

                addToast(
                    'error',
                    t('error:errorSomethingWentWrong')
                )
            }
        }
    )

    const handleForwardingToggle = () => {
        setUsernameForwarding(prev => {
            const newValue = !prev

            toggleForwardingMutation.mutate(newValue)

            return newValue
        })
    }

    const handleUsernameChange = (value: string) => {
        if (value.length > USERNAME_MAX_LENGTH) {
            return
        }

        setUsernameTaken(false)
        setNewUsername(value)
    }

    const getUsernameErrorMessage = () => {
        if (usernameTaken) {
            return t('error:thatUsernameIsTaken')
        }

        if (usernameLengthError) {
            return `Username must be between ${USERNAME_MIN_LENGTH} and ${USERNAME_MAX_LENGTH} characters.`
        }

        return ''
    }

    const handleUpdate = () => {
        if (usernameChanged && usernameValid) {
            setChangeType('username')

            updateUsername.mutate({
                username: newUsername
            })

            return
        }

        setChangeType('forwarding')
        setCurrentPage(2)
    }

    const renderCurrentPage = () => {
        switch (currentPage) {
            case 1:
                return (
                    <>
                        <InputCard
                            hasAvatar={true}
                            type='text'
                            label={t('username')}
                            value={newUsername}
                            changeFn={handleUsernameChange}
                            errorActive={
                                usernameTaken ||
                                usernameLengthError
                            }
                            errorMessage={getUsernameErrorMessage()}
                            validate='letters/numbers'
                        />

                        {userData?.role === 'model' && (
                            <div className='accountsettings__username-forwarding'>
                                <ActionCard
                                    text={t('usernameForwarding')}
                                    subtext='When enabled, your previous username will automatically redirect to your new profile.'
                                    hasSwitch
                                    toggleFn={handleForwardingToggle}
                                    toggleActive={usernameForwarding}
                                />
                            </div>
                        )}
                    </>
                )

            case 2:
                return (
                    <SuccessfulChange
                        img={Icons.avatar_big}
                        text={
                            changeType === 'username'
                                ? t(
                                      'yourUsernameHasSuccessfullyChanged'
                                  )
                                : changeType === 'forwarding'
                                  ? t(
                                        'yourUsernameForwardingUpdated'
                                    )
                                  : t(
                                        'yourUsernameAndForwardingUpdated'
                                    )
                        }
                    />
                )

            default:
                return null
        }
    }

    const renderBottomButton = () => {
        switch (currentPage) {
            case 1:
                return (
                    <FixedBottomButton
                        customClass='accountsettings__card__fixedbottombtn'
                        text={t('update')}
                        disabled={!usernameValid || usernameTaken}
                        clickFn={handleUpdate}
                    />
                )

            case 2:
                return (
                    <FixedBottomButton
                        customClass='accountsettings__card__fixedbottombtn'
                        text={t('close')}
                        clickFn={() =>
                            navigate('/settings/account')
                        }
                    />
                )

            default:
                return null
        }
    }

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t('account')}
                    title={t('changeUsername')}
                />
            }
        >
            <>
                {renderCurrentPage()}
                {renderBottomButton()}
            </>
        </WithHeaderSection>
    )
}